import { api, type Me } from './api.ts';

export interface Brand {
  name: string;
  shortName: string;
  tagline: string;
  logo: string;
}

/** Brand injected at build time (see scripts/build-web.ts). */
export const brand: Brand = (() => {
  const fallback = { name: 'Purple Management Assistant', shortName: 'Purple', tagline: 'Management Assistant', logo: '/brand/logo.svg' };
  try {
    const content = document.querySelector<HTMLMetaElement>('meta[name="pma-brand"]')?.content;
    return content ? { ...fallback, ...JSON.parse(content) } : fallback;
  } catch {
    return fallback;
  }
})();

export interface Lookup {
  id: string;
  label: string;
}

/** Session-wide state and lazily cached lookups (invalidated on writes). */
export const state = {
  me: null as Me | null,
  cache: new Map<string, Promise<Lookup[]>>(),
};

function cached(key: string, load: () => Promise<Lookup[]>): Promise<Lookup[]> {
  let entry = state.cache.get(key);
  if (!entry) {
    entry = load().catch((error) => {
      state.cache.delete(key);
      throw error;
    });
    state.cache.set(key, entry);
  }
  return entry;
}

export function invalidate(...keys: string[]): void {
  for (const key of keys) state.cache.delete(key);
}

export const lookups = {
  contacts: () =>
    cached('contacts', async () =>
      (await api.get<{ items: any[] }>('/contacts?limit=500')).items.map((c) => ({
        id: c.id,
        label: `${c.title ? `${c.title} ` : ''}${c.first_name} ${c.last_name}`,
      })),
    ),
  companies: () =>
    cached('companies', async () =>
      (await api.get<{ items: any[] }>('/companies?limit=500')).items.map((c) => ({ id: c.id, label: c.name })),
    ),
  members: () =>
    cached('members', async () =>
      (await api.get<{ members: any[] }>('/organization')).members.map((m) => ({ id: m.user_id, label: m.name })),
    ),
};

export function canWrite(): boolean {
  return !!state.me?.role && state.me.role !== 'guest';
}

/** Comments are open to guests too (client feedback on shared records). */
export function canComment(): boolean {
  return !!state.me?.role;
}

export function isAdmin(): boolean {
  return state.me?.role === 'owner' || state.me?.role === 'admin';
}
